import { Outlet } from "react-router-dom"
import { useState } from "react"
import { useBrandStore } from "@/stores"
import { Wallet, CheckCircle2, ShieldCheck } from "lucide-react"

/*
 * Login homologado (aprobado 2026-08): banda del logo del tenant + panel del
 * SISTEMA en color de MARCA (branding = lo que se vende por tenant) con nombre
 * grande + ícono + features. El color lo pone SIEMPRE la marca; la plataforma se
 * reconoce por el nombre + ícono (no por un color de sistema que rompería la marca).
 */
const SERVICE = {
  name: "Oficina Virtual",
  desc: "Comisiones, inventario y ventas de tu red de distribución.",
  feats: ["Comisiones en tiempo real", "Inventario de SIMs", "Reportes de red"],
}

function isLightColor(hex?: string): boolean {
  if (!hex) return false
  const m = hex.replace("#", "")
  if (m.length !== 6) return false
  const toLin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const r = toLin(parseInt(m.slice(0, 2), 16))
  const g = toLin(parseInt(m.slice(2, 4), 16))
  const b = toLin(parseInt(m.slice(4, 6), 16))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5
}

function mix(hex: string, other: string, t: number): string {
  const p = (h: string): [number, number, number] => {
    const s = h.replace("#", "")
    return [
      parseInt(s.slice(0, 2), 16),
      parseInt(s.slice(2, 4), 16),
      parseInt(s.slice(4, 6), 16),
    ]
  }
  const [r1, g1, b1] = p(hex)
  const [r2, g2, b2] = p(other)
  const c = (a: number, b: number) => Math.round(a * (1 - t) + b * t)
  const h = (n: number) => n.toString(16).padStart(2, "0")
  return `#${h(c(r1, r2))}${h(c(g1, g2))}${h(c(b1, b2))}`
}

/* Banda del logo: fondo OPACO adaptable (blanco / oscuro para logos casi-blancos). */
function LogoBand({ src, name }: { src: string; name: string }) {
  const [errored, setErrored] = useState(false)
  const [light, setLight] = useState<boolean | null>(null)
  const base = "flex h-[60px] w-full items-center px-8 shadow-[0_1px_0_rgba(0,0,0,0.06)]"
  if (!src || errored) {
    return (
      <div className={base} style={{ background: "#ffffff" }}>
        <span className="text-lg font-extrabold text-gray-900">{name}</span>
      </div>
    )
  }
  return (
    <div className={base} style={{ background: light === true ? "#0f1726" : "#ffffff" }}>
      <img
        src={src}
        alt={name}
        crossOrigin="anonymous"
        onError={() => setErrored(true)}
        onLoad={(e) => {
          try {
            const img = e.currentTarget
            const cv = document.createElement("canvas")
            cv.width = 32
            cv.height = 32
            const ctx = cv.getContext("2d")
            if (!ctx) return
            ctx.drawImage(img, 0, 0, 32, 32)
            const d = ctx.getImageData(0, 0, 32, 32).data
            let opaque = 0
            let nearWhite = 0
            let darkInk = 0
            for (let i = 0; i < d.length; i += 4) {
              if (d[i + 3] / 255 < 0.1) continue
              opaque++
              const L = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]
              if (L > 215) nearWhite++
              if (L < 128) darkInk++
            }
            /*
             * Banda oscura SOLO si el logo es REALMENTE blanco (NXUM): casi-blanco Y
             * sin tinta oscura. Sin el guard de tinta el umbral queda al filo (un logo
             * oscuro con fondo blanco horneado mide ~0.69-0.72 según el escalado del
             * canvas) y se pintaba una BARRA NEGRA. Con el guard hay 5x de margen.
             */
            if (opaque > 0)
              setLight(nearWhite / opaque > 0.7 && darkInk / opaque < 0.04)
          } catch {
            /* no medible → banda blanca por defecto */
          }
        }}
        className="max-h-[34px] max-w-[200px] object-contain"
      />
    </div>
  )
}

export function AuthLayout() {
  const { brandConfig } = useBrandStore()

  const brandColor = brandConfig?.primaryColor || "#2E5090"
  const brandLight = isLightColor(brandColor)
  /*
   * Marca de color CLARO: el panel MANTIENE el color de marca con degradado del
   * MISMO tono (oscurece suave, NO hacia negro → sin "degradado sucio"), con
   * texto/acentos oscuros. Marca de color OSCURO: igual que siempre.
   */
  const brandDark = brandLight
    ? mix(brandColor, "#000000", 0.16)
    : mix(brandColor, "#05070d", 0.46)
  const onBrand = brandLight ? "#0f1726" : "#ffffff"
  const onSoft = brandLight ? "rgba(20,32,53,0.72)" : "rgba(255,255,255,0.9)"
  const chipBg = brandLight ? "rgba(20,32,53,0.08)" : "rgba(255,255,255,0.16)"
  const chipBorder = brandLight ? "rgba(20,32,53,0.16)" : "rgba(255,255,255,0.3)"

  const logo = brandConfig?.logo || ""
  const name = brandConfig?.name || "Oficina Virtual"
  const panelStyle = {
    background: `linear-gradient(160deg, ${brandColor} 0%, ${brandDark} 100%)`,
    color: onBrand,
  }

  const SystemHeader = ({ compact }: { compact?: boolean }) => (
    <>
      <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: onSoft }}>
        Sistema
      </span>
      <div className="mt-1.5 mb-3 flex items-center gap-3">
        <span
          className={`${compact ? "text-2xl" : "text-[30px]"} font-extrabold leading-none tracking-tight`}
          style={{ color: onBrand }}
        >
          {SERVICE.name}
        </span>
        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
          style={{ background: chipBg, boxShadow: `inset 0 0 0 1px ${chipBorder}` }}
        >
          <Wallet size={22} style={{ color: onBrand }} />
        </span>
      </div>
      {!compact && (
        <p className="mb-5 max-w-[34ch] text-sm leading-relaxed" style={{ color: onSoft }}>
          {SERVICE.desc}
        </p>
      )}
    </>
  )

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="mx-auto flex w-full max-w-[1024px] flex-col overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-xl">
        <LogoBand src={logo} name={name} />

        <div className="flex min-h-[500px] flex-1">
          {/* IZQUIERDA — panel del SISTEMA en color de MARCA (desktop) */}
          <div className="relative hidden flex-[0_0_42%] flex-col overflow-hidden p-9 lg:flex" style={panelStyle}>
            <Wallet
              className="pointer-events-none absolute -right-6 top-6 h-[190px] w-[190px] opacity-[0.08]"
              style={{ color: onBrand }}
            />
            <div className="relative z-[1] flex flex-1 flex-col justify-center">
              <SystemHeader />
              <div className="flex flex-col gap-3">
                {SERVICE.feats.map((f) => (
                  <div key={f} className="flex items-center gap-3 text-sm font-medium">
                    <CheckCircle2 size={18} style={{ color: onBrand, opacity: 0.95 }} />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div
              className="relative z-[1] mt-6 flex items-center gap-3 rounded-[13px] border px-4 py-3"
              style={{ background: chipBg, borderColor: chipBorder }}
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]" style={{ background: chipBg }}>
                <ShieldCheck size={18} style={{ color: onBrand }} />
              </span>
              <span className="flex flex-col leading-tight">
                <b className="text-[13.5px]">Acceso seguro</b>
                <span className="text-xs" style={{ color: onSoft }}>
                  Tus comisiones protegidas
                </span>
              </span>
            </div>
          </div>

          {/* DERECHA — form (Outlet) */}
          <div className="flex flex-1 flex-col">
            <div className="px-6 pb-4 pt-5 lg:hidden" style={panelStyle}>
              <SystemHeader compact />
            </div>
            <div className="flex flex-1 flex-col justify-center px-8 py-9 lg:px-10">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
